import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import ProductPreview from "@/components/ProductPreview";
import { ArrowLeft, CaretLeft, CaretRight, PlayCircle, WhatsappLogo, X } from "@phosphor-icons/react";

const TOUR_STEPS = {
  "bank-reconciliation-suite": [
    { title: "Import your bank statements", body: "Upload Nostro, cards, EFT and clearing files — or connect via SWIFT/API. We auto-detect the format." },
    { title: "Auto-match runs live", body: "Our engine matches on amount, reference, narration and value date — with fuzzy rules for common noise." },
    { title: "Work your exceptions", body: "The 4% that don't match land in a workspace with side-by-side compare, one-click assign and 6-eye approval." },
    { title: "Sign off & audit", body: "Post journals, export the audit pack, and hand to internal audit — no spreadsheets, ever." },
  ],
  "hospital-clinic-hms": [
    { title: "Register a patient", body: "Front-desk captures ID, phone, insurance and TPA card in under 30 seconds." },
    { title: "Doctor consults with EMR", body: "OPD/IPD workflow with vitals, prescriptions and lab orders — voice-to-text ready." },
    { title: "Pharmacy & lab dispense", body: "Orders flow to pharmacy and lab automatically with stock and pricing rules." },
    { title: "Bill and settle claims", body: "Cash, card, TPA pre-auth and cashless — all in one screen with tax and audit trail." },
  ],
};

const DEFAULT_STEPS = (title) => ([
  { title: `Onboard your ${title} tenant`, body: "10-minute guided setup with your logo, users and initial data." },
  { title: "Configure your workflow", body: "Turn on the modules you need — everything else stays out of the way." },
  { title: "Go live", body: "Import data, train the team on our 45-min certification, and switch on." },
  { title: "Grow with the product", body: "Monthly product updates plus a customer success owner on WhatsApp." },
]);

export default function ProductTour() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    api.get(`/products/slug/${slug}`).then((r) => setProduct(r.data)).catch(() => {});
    const stored = JSON.parse(localStorage.getItem("maxine_demo_tokens") || "{}");
    if (!stored[slug]) nav(`/products/${slug}?demo=1`, { replace: true });
  }, [slug, nav]);

  if (!product) return <div className="min-h-screen bg-white" />;

  const steps = (product.tour_steps && product.tour_steps.length > 0)
    ? product.tour_steps.map((s) => ({ title: s.title || "Step", body: s.body || "" }))
    : (TOUR_STEPS[slug] || DEFAULT_STEPS(product.title));
  const total = steps.length;
  const cur = steps[step];

  return (
    <div className="min-h-screen bg-[color:var(--paper-alt)]">
      <SEO title={`${product.title} — Interactive tour`} />
      <Nav />
      <main className="pt-24 md:pt-32 pb-20">
        <div className="container-x">
          <div className="flex items-center justify-between">
            <Link to={`/products/${slug}/demo`} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-neutral-500 hover:text-[color:var(--brand)]">
              <ArrowLeft size={14} weight="bold" /> Back to live snapshot
            </Link>
            <button onClick={() => nav(`/products/${slug}`)} data-testid="tour-close" className="text-neutral-500 hover:text-[color:var(--ink)] p-2"><X size={16} /></button>
          </div>

          <div className="mt-8 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="eyebrow">Interactive tour</div>
              <h1 className="mt-3 font-display font-black text-4xl md:text-5xl leading-tight">{product.title}</h1>
              <p className="mt-3 text-neutral-700">{product.tagline}</p>
              <div className="mt-8 space-y-2" data-testid="tour-stepper">
                {steps.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`tour-step-${i}`}
                    onClick={() => setStep(i)}
                    className={`w-full text-left border-l-2 pl-4 py-3 transition-all ${i === step ? "border-[color:var(--brand)] bg-white" : "border-[color:var(--line)] hover:border-neutral-400 hover:bg-white"}`}
                  >
                    <div className="font-mono text-[10px] tracking-widest text-neutral-500">STEP 0{i + 1}</div>
                    <div className={`mt-1 font-display font-semibold text-sm ${i === step ? "text-[color:var(--ink)]" : "text-neutral-600"}`}>{s.title}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="border border-[color:var(--line)] bg-white p-8 md:p-10 min-h-[420px] flex flex-col">
                <div className="font-mono text-[11px] uppercase tracking-widest text-[color:var(--brand)]">Step {step + 1} of {total}</div>
                <h2 data-testid="tour-title" className="mt-3 font-display font-black text-3xl md:text-4xl leading-tight">{cur.title}</h2>
                <p data-testid="tour-body" className="mt-4 text-neutral-700 leading-relaxed max-w-2xl text-lg">{cur.body}</p>

                <div className="mt-8 flex-1 border border-[color:var(--line)] bg-[color:var(--paper-alt)] relative min-h-[280px]" data-testid="tour-preview">
                  <div className="w-full h-full">
                    <ProductPreview preview_key={product.preview_key} />
                  </div>
                  <div className="absolute -top-3 left-4 bg-[color:var(--brand)] text-white font-mono text-[10px] uppercase tracking-widest px-2 py-1 shadow whitespace-nowrap max-w-[240px] truncate">
                    Step {step + 1}
                  </div>
                  <div className="absolute -top-3 right-4 bg-white border border-[color:var(--line)] font-mono text-[10px] tracking-widest text-neutral-600 px-2 py-1">
                    {product.industry}
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-[color:var(--line)] flex items-center justify-between gap-4">
                  <button
                    data-testid="tour-prev"
                    onClick={() => setStep(Math.max(0, step - 1))}
                    disabled={step === 0}
                    className="inline-flex items-center gap-2 py-2 px-3 border border-[color:var(--ink)] hover:bg-[color:var(--ink)] hover:text-white transition-colors disabled:opacity-30 disabled:pointer-events-none text-sm font-semibold"
                  >
                    <CaretLeft size={14} weight="bold" /> Previous
                  </button>
                  <div className="flex-1 mx-2 h-0.5 bg-[color:var(--line)]">
                    <div className="h-0.5 bg-[color:var(--brand)] transition-all" style={{ width: `${((step + 1) / total) * 100}%` }} />
                  </div>
                  {step < total - 1 ? (
                    <button data-testid="tour-next" onClick={() => setStep(step + 1)} className="btn-brand text-sm py-2 px-3">
                      Next <CaretRight size={14} weight="bold" />
                    </button>
                  ) : (
                    <a
                      href={`https://wa.me/7054986771?text=${encodeURIComponent(`I finished the ${product.title} tour — let's book a live walkthrough.`)}`}
                      target="_blank" rel="noreferrer" data-testid="tour-finish"
                      className="btn-brand text-sm py-2 px-3"
                    >
                      <WhatsappLogo size={14} weight="fill" /> Book a walkthrough
                    </a>
                  )}
                </div>
              </div>
              <Link to={`/products/${slug}/demo`} className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-neutral-500 hover:text-[color:var(--brand)]">
                <PlayCircle size={14} weight="bold" /> Prefer the read-only live snapshot?
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
